import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Camera, AlertCircle, X, Users, Sparkles, CheckCircle2, RefreshCw } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Textarea, Spinner } from "@/components/ui";
import type { Json } from "@/lib/database.types";
import {
  ACCEPTED_PHOTO_MIMES, licencePhotoPath,
  CATEGORY_DISPLAY, CATEGORY_ORDER,
} from "./licenceHelpers";

// =============================================================================
// NewLicenceScreen — /licences/new
// Add a licence to the current user's record. Handles:
//   - Loading all active licence_types (64 rows) grouped by category
//   - Per-type dynamic form (expiry required? back photo required? etc.)
//   - Upload-first-then-RPC flow: photos upload to Supabase Storage at
//     {employee_id}/{session_uuid}/{side}_{ts}.{ext} before calling
//     app.add_licence so we can pass the resolved storage paths in one call.
//   - Director verification: queued via app.add_licence's notification outbox
//     write (if licence_types.requires_verification).
// =============================================================================

const AU_STATES = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"] as const;

interface LicenceType {
  licence_type_id:        string;
  code:                   string;
  display_name:           string;
  category:               string;
  issuing_body:           string | null;
  requires_renewal:       boolean;
  typical_validity_years: number | null;
  requires_photo:         boolean;
  requires_back_photo:    boolean;
  requires_verification:  boolean;
  external_system:        string | null;
  notes:                  string | null;
  is_active:              boolean;
  sort_order:             number;
}

export default function NewLicenceScreen() {
  const user        = useRequireAuth();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  // On-behalf mode: /hr/licences/:employeeId/new has employeeId set; self-add
  // route /licences/new doesn't. Both land here.
  const { employeeId: routeEmployeeId } = useParams<{ employeeId?: string }>();
  const targetEmployeeId = routeEmployeeId ?? user?.employee_id ?? null;
  const isOnBehalf = !!routeEmployeeId && routeEmployeeId !== user?.employee_id;
  const canActOnBehalf = hasRole(user, "manager");

  // If on-behalf but caller isn't mgr+, redirect implicitly — the RPC would
  // reject anyway. Simpler to gate in the UI.
  if (isOnBehalf && !canActOnBehalf) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-sm text-text-muted">
            You need manager-or-above access to add a licence for another employee.
          </div>
        </Card>
      </div>
    );
  }

  // When on-behalf, fetch the target employee's name for the header
  const { data: targetEmployee } = useQuery({
    queryKey: ["new_licence_target_employee", routeEmployeeId],
    enabled: !!routeEmployeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, email")
        .eq("employee_id", routeEmployeeId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // ---------------------------------------------------------------------------
  // T-391 — renew mode. The Renew button on the HR detail screen links here
  // with ?renewType/&renewNumber/&renewState so type + details are prefilled.
  // Nothing else changes: app.add_licence auto-archives the old active row of
  // the same type (T-238 / mig 136), so saving IS the renewal.
  // ---------------------------------------------------------------------------
  const [searchParams] = useSearchParams();
  const renewTypeId = searchParams.get("renewType");
  const isRenewal   = !!renewTypeId;

  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------
  const [licenceTypeId, setLicenceTypeId] = useState<string>(renewTypeId ?? "");
  const [licenceNumber, setLicenceNumber] = useState(searchParams.get("renewNumber") ?? "");
  const [issueDate,     setIssueDate]     = useState("");
  const [expiryDate,    setExpiryDate]    = useState("");
  const [stateIssued,   setStateIssued]   = useState(searchParams.get("renewState") ?? "");
  const [externalUrl,   setExternalUrl]   = useState("");
  const [notes,         setNotes]         = useState("");
  const [frontPhoto,    setFrontPhoto]    = useState<File | null>(null);
  const [backPhoto,     setBackPhoto]     = useState<File | null>(null);
  const [submitError,   setSubmitError]   = useState<string | null>(null);

  // T-153: AI extraction flow. We upload the front photo to Storage on pick,
  // then call /licences/ai-extract with the storage path. Result pre-fills
  // the type picker + details below. Pattern mirrors AP receipts.
  type AiStatus = "idle" | "uploading" | "extracting" | "done" | "error";
  const [aiStatus,        setAiStatus]        = useState<AiStatus>("idle");
  const [aiConfidence,    setAiConfidence]    = useState<number | null>(null);
  const [aiNote,          setAiNote]          = useState<string | null>(null);
  const [aiError,         setAiError]         = useState<string | null>(null);
  const [frontStoragePath, setFrontStoragePath] = useState<string | null>(null);
  const [backStoragePath,  setBackStoragePath]  = useState<string | null>(null);
  // Generated once per session — same path prefix for front + back.
  const submissionUuidRef = useRef<string>(crypto.randomUUID());
  // Track which file we last AI-extracted, to avoid re-running on unrelated re-renders.
  const lastExtractedFrontRef = useRef<File | null>(null);

  // ---------------------------------------------------------------------------
  // Load licence_types
  // ---------------------------------------------------------------------------
  const { data: licenceTypes, isLoading: typesLoading } = useQuery({
    queryKey: ["licence_types_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("licence_types")
        .select(`
          licence_type_id, code, display_name, category, issuing_body,
          requires_renewal, typical_validity_years,
          requires_photo, requires_back_photo, requires_verification,
          external_system, notes, is_active, sort_order
        `)
        .eq("is_active", true)
        .order("sort_order")
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as unknown as LicenceType[];
    },
  });

  const selectedType = useMemo(
    () => licenceTypes?.find(t => t.licence_type_id === licenceTypeId) ?? null,
    [licenceTypes, licenceTypeId],
  );

  const typesGrouped = useMemo(() => {
    const g = new Map<string, LicenceType[]>();
    if (!licenceTypes) return g;
    for (const t of licenceTypes) {
      if (!g.has(t.category)) g.set(t.category, []);
      g.get(t.category)!.push(t);
    }
    return g;
  }, [licenceTypes]);

  // -----------------------------------------------------------------------------
  // T-153 — Upload-on-pick + AI extract.
  //
  // When the user picks a FRONT photo, we:
  //   1. Upload it to Supabase Storage at the canonical path.
  //   2. POST /licences/ai-extract on the worker → Claude Haiku reads it.
  //   3. Pre-fill the form fields with the extracted values.
  //
  // BACK photo gets uploaded on pick but no AI run (front is enough to identify).
  //
  // We use the same submissionUuidRef so front + back share a folder. If the
  // user replaces a photo, we upload to a NEW filename within that folder
  // (timestamped) — the old one is orphaned but harmless (cron cleanup later).
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (!frontPhoto || !targetEmployeeId) return;
    // Don't re-run for the same file
    if (lastExtractedFrontRef.current === frontPhoto) return;
    lastExtractedFrontRef.current = frontPhoto;

    let cancelled = false;
    (async () => {
      try {
        setAiError(null);
        setAiStatus("uploading");
        const path = licencePhotoPath(targetEmployeeId, submissionUuidRef.current, "front", frontPhoto);
        const { error: upErr } = await supabase.storage
          .from("licence-documents")
          .upload(path, frontPhoto, { contentType: frontPhoto.type, upsert: false });
        if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
        if (cancelled) return;
        setFrontStoragePath(path);

        setAiStatus("extracting");
        const res = await apiFetch("/licences/ai-extract", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ storage_path: path }),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`AI extraction failed (${res.status}): ${txt.slice(0, 800)}`);
        }
        const ai = await res.json() as {
          confidence: number;
          licence_type_id: string | null;
          extracted: {
            licence_number?: string | null;
            issue_date?:     string | null;
            expiry_date?:    string | null;
            state_issued?:   string | null;
            notes?:          string | null;
          };
        };
        if (cancelled) return;

        setAiConfidence(ai.confidence);
        setAiNote(ai.extracted?.notes ?? null);

        // Only pre-fill empty fields — don't clobber user edits if they typed
        // something while extraction was running.
        if (ai.licence_type_id && !licenceTypeId) {
          setLicenceTypeId(ai.licence_type_id);
        }
        // T-391 exception: renew mode prefills the OLD licence number as a
        // convenience, but reissued certs (RTO CPR/First Aid etc) usually
        // carry a NEW number — let the AI's fresh read replace an UNTOUCHED
        // prefill. A number the user actually typed still wins.
        const numberIsUntouchedRenewPrefill =
          isRenewal && licenceNumber === (searchParams.get("renewNumber") ?? "");
        if (ai.extracted?.licence_number && (!licenceNumber || numberIsUntouchedRenewPrefill)) {
          setLicenceNumber(ai.extracted.licence_number);
        }
        if (ai.extracted?.issue_date && !issueDate) {
          setIssueDate(ai.extracted.issue_date);
        }
        if (ai.extracted?.expiry_date && !expiryDate) {
          setExpiryDate(ai.extracted.expiry_date);
        }
        if (ai.extracted?.state_issued && !stateIssued) {
          setStateIssued(ai.extracted.state_issued);
        }
        setAiStatus("done");
      } catch (e: any) {
        if (cancelled) return;
        setAiError(e?.message ?? "Couldn't read the photo");
        setAiStatus("error");
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontPhoto, targetEmployeeId]);

  // Back photo: upload only, no AI (front already identified the licence).
  useEffect(() => {
    if (!backPhoto || !targetEmployeeId) return;
    if (backStoragePath) return; // already uploaded
    let cancelled = false;
    (async () => {
      const path = licencePhotoPath(targetEmployeeId, submissionUuidRef.current, "back", backPhoto);
      const { error } = await supabase.storage
        .from("licence-documents")
        .upload(path, backPhoto, { contentType: backPhoto.type, upsert: false });
      if (!cancelled && !error) setBackStoragePath(path);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backPhoto, targetEmployeeId]);

  // Client-side validation mirrors app.add_licence's server-side rules
  const validationError = useMemo(() => {
    if (!selectedType) return "Pick a licence type to continue";
    if (selectedType.requires_renewal && !expiryDate) {
      return `${selectedType.display_name} requires an expiry date`;
    }
    if (selectedType.requires_photo && !frontPhoto) {
      return `${selectedType.display_name} requires a front photo`;
    }
    if (selectedType.requires_back_photo && !backPhoto) {
      return `${selectedType.display_name} requires both front and back photos`;
    }
    return null;
  }, [selectedType, expiryDate, frontPhoto, backPhoto]);

  // ---------------------------------------------------------------------------
  // Submit — upload photos first, then RPC
  // ---------------------------------------------------------------------------
  const mutation = useMutation({
    mutationFn: async () => {
      if (!targetEmployeeId) {
        throw new Error(
          isOnBehalf
            ? "No target employee selected."
            : "Your login isn't linked to an employee record. Ask a director."
        );
      }
      if (!selectedType) throw new Error("No licence type selected.");

      // T-153 — photos were uploaded on pick (see useEffects above) so the
      // storage paths are already set. Fallback path: if for some reason the
      // pre-upload didn't run (e.g. JS race, AI flow short-circuited), upload
      // here as a belt-and-braces.
      let frontPath = frontStoragePath;
      if (frontPhoto && !frontPath) {
        const path = licencePhotoPath(targetEmployeeId, submissionUuidRef.current, "front", frontPhoto);
        const { error: upErr } = await supabase.storage
          .from("licence-documents")
          .upload(path, frontPhoto, { contentType: frontPhoto.type, upsert: false });
        if (upErr) throw new Error(`Front photo upload failed: ${upErr.message}`);
        frontPath = path;
      }

      let backPath = backStoragePath;
      if (backPhoto && !backPath) {
        const path = licencePhotoPath(targetEmployeeId, submissionUuidRef.current, "back", backPhoto);
        const { error: upErr } = await supabase.storage
          .from("licence-documents")
          .upload(path, backPhoto, { contentType: backPhoto.type, upsert: false });
        if (upErr) throw new Error(`Back photo upload failed: ${upErr.message}`);
        backPath = path;
      }

      const payload = {
        licence_type_id: licenceTypeId,
        licence_number:  licenceNumber || null,
        issue_date:      issueDate     || null,
        expiry_date:     expiryDate    || null,
        state_issued:    stateIssued   || null,
        front_photo_url: frontPath,
        back_photo_url:  backPath,
        notes:           notes         || null,
        external_url:    externalUrl   || null,
      };

      const { data, error } = await supabase
        .schema("app")
        .rpc("add_licence", {
          p_employee_id: targetEmployeeId,
          p_payload:     payload as unknown as Json,
        });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my_licences"] });
      queryClient.invalidateQueries({ queryKey: ["hr_licences_all"] });
      queryClient.invalidateQueries({ queryKey: ["hr_licences_detail", targetEmployeeId] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_pending_licences"] });
      // On-behalf → back to the employee's detail page; self-add → /licences
      navigate(isOnBehalf ? `/hr/licences/${targetEmployeeId}` : "/licences");
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message
        : (err as { message?: string })?.message ?? "Submission failed — please try again.";
      setSubmitError(msg);
    },
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (typesLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto pb-24">
      <div className="mb-6">
        <Link
          to={isOnBehalf ? `/hr/licences/${targetEmployeeId}` : "/licences"}
          className="text-text-muted text-sm flex items-center gap-1 hover:text-text"
        >
          <ArrowLeft size={14} /> Back to {isOnBehalf ? "licence detail" : "licences"}
        </Link>
        <h1 className="text-2xl font-semibold mt-2">
          {isOnBehalf
            ? `Add licence for ${[targetEmployee?.first_name, targetEmployee?.last_name].filter(Boolean).join(" ") || "employee"}`
            : "Add a licence"}
        </h1>
        <p className="text-sm text-text-muted mt-1">
          {isOnBehalf
            ? "Adding on-behalf as a manager — the employee will see this licence on their /licences page after submission."
            : "Capture a licence, induction, or competency you hold."}
          {selectedType?.requires_verification && !isOnBehalf && (
            <> A director will be notified to verify once you submit.</>
          )}
          {selectedType?.requires_verification && isOnBehalf && (
            <> A director will still need to verify after submission — even though you're adding it on-behalf.</>
          )}
        </p>

        {isOnBehalf && targetEmployee && (
          <Card className="mt-3 border-accent/30">
            <div className="p-3 flex items-center gap-2 text-sm">
              <Users size={14} className="text-accent" />
              <span>
                On-behalf of <strong>{targetEmployee.first_name} {targetEmployee.last_name}</strong>
                {targetEmployee.email && <> · {targetEmployee.email}</>}
              </span>
            </div>
          </Card>
        )}

        {/* T-391 — renew mode banner */}
        {isRenewal && (
          <Card className="mt-3 border-accent/30">
            <div className="p-3 flex items-start gap-2 text-sm">
              <RefreshCw size={14} className="text-accent mt-0.5 flex-shrink-0" />
              <span>
                <strong>Renewal</strong>
                {selectedType && <> — {selectedType.display_name}</>}.
                Type and details are prefilled — just attach the new cert and update
                the dates. The old one is archived automatically when you save;
                nothing needs deleting and the history is kept.
              </span>
            </div>
          </Card>
        )}
      </div>

      {/* PHOTO + AI EXTRACT — top of form. Pick the photo and the AI fills
          the rest. T-153 (James 2026-04-29). */}
      <Card className="mb-4">
        <div className="p-6 space-y-4">
          <div>
            <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 flex items-center gap-1.5">
              <Sparkles size={12} className="text-accent"/> Snap the licence — AI fills the rest
            </h2>
            <p className="text-xs text-text-muted mt-1">
              Take or upload a clear photo of the front (and back if there is one). The AI
              reads the card and pre-fills the form below — you just review and submit.
              JPEG / PNG / WebP / PDF, up to 10 MB.
            </p>
          </div>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <PhotoPicker label="Front" file={frontPhoto} onChange={setFrontPhoto} />
            {/* PDFs are typically a single-page certificate with no back side —
                hide the back picker when front is a PDF. */}
            {frontPhoto?.type !== "application/pdf" && (
              <PhotoPicker label="Back (optional)" file={backPhoto} onChange={setBackPhoto} />
            )}
          </div>

          {/* AI status banner */}
          {aiStatus === "uploading" && (
            <div className="flex items-center gap-2 text-[12px] text-text-muted px-3 py-2 bg-ink-raised rounded border border-line">
              <Spinner size={12}/> Uploading photo…
            </div>
          )}
          {aiStatus === "extracting" && (
            <div className="flex items-center gap-2 text-[12px] text-accent px-3 py-2 bg-accent/8 rounded border border-accent/20">
              <Spinner size={12}/> Reading the licence with AI…
            </div>
          )}
          {aiStatus === "done" && aiConfidence !== null && (
            <div className={`flex items-start gap-2 text-[12px] px-3 py-2 rounded border ${
              aiConfidence >= 0.7
                ? "text-ok bg-ok/10 border-ok/20"
                : aiConfidence >= 0.5
                  ? "text-warn bg-warn/10 border-warn/20"
                  : "text-lost bg-lost/10 border-lost/20"
            }`}>
              <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0"/>
              <div>
                <div className="font-medium">
                  Read with {Math.round(aiConfidence * 100)}% confidence
                  {aiConfidence < 0.6 && " — please double-check the details below"}
                </div>
                {aiNote && <div className="mt-0.5 italic">{aiNote}</div>}
              </div>
            </div>
          )}
          {aiStatus === "error" && (
            <div className="flex items-start gap-2 text-[12px] text-lost px-3 py-2 bg-lost/10 rounded border border-lost/20">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
              <div>
                <div className="font-medium">Couldn't read the photo automatically</div>
                <div className="mt-0.5">{aiError ?? "Please fill the details manually below."}</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Type picker */}
      <Card className="mb-4">
        <div className="p-6 space-y-4">
          <Field label="Licence type *">
            <Select value={licenceTypeId} onChange={e => setLicenceTypeId(e.target.value)}>
              <option value="">— Select —</option>
              {CATEGORY_ORDER
                .filter(cat => typesGrouped.has(cat))
                .map(cat => (
                  <optgroup key={cat} label={CATEGORY_DISPLAY[cat] ?? cat}>
                    {typesGrouped.get(cat)!.map(t => (
                      <option key={t.licence_type_id} value={t.licence_type_id}>
                        {t.display_name}
                      </option>
                    ))}
                  </optgroup>
                ))}
            </Select>
          </Field>

          {selectedType && (
            <div className="text-xs text-text-muted p-3 bg-ink-raised rounded border border-line space-y-1">
              {selectedType.notes && <p>{selectedType.notes}</p>}
              {selectedType.issuing_body && (
                <p className="font-mono text-[11px]">Issued by: {selectedType.issuing_body}</p>
              )}
              {selectedType.external_system && (
                <p>
                  Managed in <span className="font-medium text-text">{selectedType.external_system}</span>.
                  Paste your portal link below if you have one.
                </p>
              )}
              {!selectedType.requires_renewal && (
                <p>This licence type doesn't require an expiry date.</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Details */}
      {selectedType && (
        <Card className="mb-4">
          <div className="p-6 space-y-4">
            <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2">
              Licence details
            </h2>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              <Field label="Licence number">
                <Input
                  type="text"
                  value={licenceNumber}
                  onChange={e => setLicenceNumber(e.target.value)}
                  placeholder="e.g. 12345678"
                />
              </Field>
              <Field label="State of issue">
                <Select value={stateIssued} onChange={e => setStateIssued(e.target.value)}>
                  <option value="">—</option>
                  {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Issue date">
                <Input
                  type="date"
                  value={issueDate}
                  onChange={e => setIssueDate(e.target.value)}
                />
              </Field>
              <Field label={`Expiry date${selectedType.requires_renewal ? " *" : ""}`}>
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                />
              </Field>
              <div className="md:col-span-2">
                <Field
                  label="External portal URL"
                  hint={
                    selectedType.external_system
                      ? `Paste your link to ${selectedType.external_system}`
                      : "Optional — link to the authoritative record if it lives elsewhere"
                  }
                >
                  <Input
                    type="url"
                    value={externalUrl}
                    onChange={e => setExternalUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Notes">
                  <Textarea
                    rows={2}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Anything worth knowing"
                  />
                </Field>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* (Photos moved to top of form — see "Snap the licence" card above.
          The selectedType.requires_back_photo gate is now enforced via
          validationError instead of conditionally rendering the picker.) */}

      {/* Sticky submit bar — always rendered once a type is selected */}
      {selectedType && (
        <div className="sticky bottom-4 max-md:bottom-[calc(var(--nav-clear)+1rem)] z-10">
          <Card className="shadow-lg">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1 text-sm">
                {submitError ? (
                  <div className="flex items-center gap-2 text-danger">
                    <AlertCircle size={14} className="flex-shrink-0" />
                    <span className="truncate">{submitError}</span>
                  </div>
                ) : validationError ? (
                  <span className="text-text-muted">{validationError}</span>
                ) : (
                  <span className="text-text-muted">Ready to submit.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link to={isOnBehalf ? `/hr/licences/${targetEmployeeId}` : "/licences"}>
                  <Button variant="ghost" disabled={mutation.isPending}>
                    Cancel
                  </Button>
                </Link>
                <Button
                  variant="primary"
                  icon={Save}
                  disabled={!!validationError || mutation.isPending}
                  onClick={() => {
                    setSubmitError(null);
                    mutation.mutate();
                  }}
                >
                  {mutation.isPending ? "Submitting…" : "Submit licence"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PhotoPickerProps {
  label:    string;
  file:     File | null;
  onChange: (f: File | null) => void;
}

function PhotoPicker({ label, file, onChange }: PhotoPickerProps) {
  const previewUrl = file ? URL.createObjectURL(file) : null;
  const isImage    = file?.type.startsWith("image/");

  return (
    <div>
      <label className="text-[11px] font-medium text-text-muted mb-1 block">
        {label}
      </label>
      <div className="border border-line rounded-md p-3 bg-ink-raised">
        {!file ? (
          <label className="flex flex-col items-center justify-center gap-2 py-6 cursor-pointer">
            <Camera size={22} className="text-text-muted" />
            <span className="text-sm text-text-muted">Take a photo or upload</span>
            {/* capture="environment" hints the browser to use the rear camera on
                mobile. Desktop ignores the attribute and shows a normal file
                picker. Most mobile browsers still offer a Files option alongside
                Camera when you tap. */}
            <input
              type="file"
              accept={ACCEPTED_PHOTO_MIMES}
              capture="environment"
              className="hidden"
              onChange={e => onChange(e.target.files?.[0] ?? null)}
            />
          </label>
        ) : (
          <div className="flex items-start gap-3">
            {previewUrl && isImage ? (
              <img
                src={previewUrl}
                alt="preview"
                className="w-20 h-20 object-cover rounded border border-line flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 flex items-center justify-center bg-ink-card rounded border border-line text-text-muted text-[10px] font-mono flex-shrink-0">
                {file.type === "application/pdf" ? "PDF" : "FILE"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{file.name}</div>
              <div className="text-[11px] text-text-muted">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </div>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-[11px] text-accent hover:underline mt-1 flex items-center gap-1"
              >
                <X size={12} /> Replace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
